import { useCallback } from 'react';
import * as THREE from 'three';
import type { WorkspaceState, WorkspaceCallbacks } from '../../utils/workspace.types';
import { useTrussStore } from '../../../../hooks/useTrussManagement';

/**
 * Beam operations hook - handles all beam-related functionality
 * Extracted from STAADWorkspace3D to maintain exact behavior
 * Enhanced with beam truss (distributed load) support
 */
export const useBeamOperations = (
  state: WorkspaceState,
  _callbacks: WorkspaceCallbacks,
  modelStore: any,
  uiStore: any
) => {
  // Get truss store for checking if truss tool is active
  const trussStore = useTrussStore();
  // Handle beam creation between nodes
  const handleBeamCreation = useCallback((startNodeId: string, endNodeId: string) => {
    if (!modelStore?.addBeam) return;

    //console.log('🎯 Creating beam between nodes:', startNodeId, endNodeId);
    
    try {
      modelStore.addBeam(startNodeId, endNodeId);
      
      // Show success notification
      if (uiStore?.showNotification) {
        uiStore.showNotification('Beam created successfully', 'success');
      }
    } catch (error) {
      console.error('Error creating beam:', error);
      if (uiStore?.showNotification) {
        uiStore.showNotification('Failed to create beam', 'error');
      }
    }
  }, [modelStore, uiStore]);

  // Handle beam click
  const handleBeamClick = useCallback((beamId: string, event?: any) => {
    //console.log('🎯 Beam clicked:', beamId, 'truss tool active:', trussStore.activeTool);
    
    if (event && typeof event === 'object' && event.stopPropagation) {
      event.stopPropagation();
    }
    
    // Check if truss tool is active - this enables beam truss functionality
    if (trussStore.activeTool) {
      //console.log('🎯 Truss tool is active, beam clicked for distributed load:', beamId);
      
      // Trigger beam truss configuration
      if (uiStore?.showBeamTrussConfigPanel) {
        uiStore.showBeamTrussConfigPanel(beamId);
      }
      return;
    }
    
    // Check if in perpendicular mode
    if (state.isBeamCreationMode && uiStore?.isPerpModeActive) {
      if (uiStore.setSelectedBeamForPerp) {
        uiStore.setSelectedBeamForPerp(beamId);
        //console.log('🎯 Selected beam for perpendicular mode:', beamId);
      }
      return;
    }
    
    // Normal beam selection
    if (state.isShiftPressed && modelStore?.toggleBeamSelection) {
      modelStore.toggleBeamSelection(beamId);
    } else if (modelStore?.selectBeam) {
      modelStore.selectBeam(beamId);
    }
  }, [state.isBeamCreationMode, state.isShiftPressed, modelStore, uiStore, trussStore.activeTool]);

  // Handle beam hover
  const handleBeamHover = useCallback((beamId: string | null) => {
    if (modelStore?.setHoveredBeam) {
      modelStore.setHoveredBeam(beamId);
    }
  }, [modelStore]);

  // Handle beam center click for node creation
  const handleBeamCenterClick = useCallback((beamId: string, clickPoint?: THREE.Vector3) => {
    if (!state.isNodeCreationMode || !modelStore?.addNodeAtBeamCenter) return;

    //console.log('🎯 Creating node at beam center:', beamId);
    
    try {
      const success = modelStore.addNodeAtBeamCenter(beamId, clickPoint);
      
      if (success) {
        if (uiStore?.showNotification) {
          uiStore.showNotification('Node created at beam center', 'success');
        }
      } else {
        if (uiStore?.showNotification) {
          uiStore.showNotification('Failed to create node at beam center', 'error');
        }
      }
    } catch (error) {
      console.error('Error creating node at beam center:', error);
      if (uiStore?.showNotification) {
        uiStore.showNotification('Error creating node at beam center', 'error');
      }
    }
  }, [state.isNodeCreationMode, modelStore, uiStore]);

  // Handle beam intersection detection
  const handleBeamIntersection = useCallback((_beam1Id: string, _beam2Id: string) => {
    //console.log('🎯 Checking beam intersection:', _beam1Id, _beam2Id);
    
    // This would implement the intersection detection logic
    // For now, just log the intersection
    if (uiStore?.showNotification) {
      uiStore.showNotification('Beam intersection detected', 'info');
    }
  }, [uiStore]);

  // Delete beam
  const handleBeamDelete = useCallback((beamId: string) => {
    if (!modelStore?.deleteBeam) return;

    //console.log('🎯 Deleting beam:', beamId);
    
    try {
      modelStore.deleteBeam(beamId);
      
      if (uiStore?.showNotification) {
        uiStore.showNotification('Beam deleted', 'success');
      }
    } catch (error) {
      console.error('Error deleting beam:', error);
      if (uiStore?.showNotification) {
        uiStore.showNotification('Failed to delete beam', 'error');
      }
    }
  }, [modelStore, uiStore]);

  return {
    handleBeamCreation,
    handleBeamClick,
    handleBeamHover,
    handleBeamCenterClick,
    handleBeamIntersection,
    handleBeamDelete
  };
};
